import { useEffect, useRef, useState } from "react";
import { Play, Pause, Radio } from "lucide-react";
import api from "@/lib/api";
import { SectionHeading } from "@/components/Section";

export default function DailyBriefing() {
  const [brief, setBrief] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/briefing").then(({ data }) => setBrief(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.duration ? el.currentTime / el.duration : 0);
    const onEnd = () => { setPlaying(false); setProgress(0); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => { el.removeEventListener("timeupdate", onTime); el.removeEventListener("ended", onEnd); };
  }, [brief]);

  if (!brief) return null;

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); } else { el.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * el.duration;
  };

  const dayLabel = new Date(`${brief.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <section className="border-b border-bline bg-ink/40" data-testid="briefing-section">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-12">
        <SectionHeading index="AU" eyebrow="DAILY AUDIO" title="The Morning Huddle" />
        <div className="flex items-center gap-5 rounded-xl border border-bline bg-surface p-5">
          <button
            type="button"
            data-testid="briefing-play-btn"
            onClick={toggle}
            aria-label={playing ? "Pause the briefing" : "Play the briefing"}
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-volt text-ink transition-transform hover:scale-105"
          >
            {playing ? <Pause className="h-6 w-6" /> : <Play className="ml-0.5 h-6 w-6" />}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Radio className={`h-3.5 w-3.5 ${playing ? "animate-pulse text-volt" : "text-slate-500"}`} />
              <p className="font-score text-[10px] tracking-widest text-slate-500" data-testid="briefing-date">
                {dayLabel.toUpperCase()}{brief.is_today ? " · TODAY" : " · LATEST"} · {brief.games} GAME{brief.games === 1 ? "" : "S"}
              </p>
            </div>
            <div
              className="mt-3 h-1.5 w-full cursor-pointer overflow-hidden rounded-full bg-ink"
              data-testid="briefing-progress"
              onClick={seek}
              role="progressbar"
              aria-valuenow={Math.round(progress * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="h-full rounded-full bg-volt transition-[width]" style={{ width: `${progress * 100}%` }} />
            </div>
            <p className="mt-2 truncate font-score text-[10px] text-slate-600">
              YOUR DFW RUNDOWN IN ABOUT A MINUTE — GAMES, TIMES AND LINES
            </p>
          </div>
        </div>
        <audio ref={audioRef} src={brief.audio_url} preload="none" data-testid="briefing-audio" />
      </div>
    </section>
  );
}
